import { useMemo, type CSSProperties } from "react";
import { useLayoutColors } from "@/hooks/useLayoutColors";

export type GridAppearance = {
  /** Default row / cell background. Falls back to theme table row color. */
  rowBackground?: string;
  /** Row background on hover. Falls back to theme table row hover color. */
  rowHoverBackground?: string;
  /** Header row / cell background. Falls back to theme table header color. */
  headerBackground?: string;
  /** Cell background override. Defaults to `rowBackground` when unset. */
  cellBackground?: string;
};

/** Colors for the node diff (delta) view grid, as CSS. */
export type DeltaAppearance = {
  /** Label, before, after, and arrow cells. Default the surface. */
  cellBackground?: string;
  /** 1px grid gaps between cells. Default the divider. */
  gridBackground?: string;
  /** Collapsed "N unchanged fields" row. Defaults to `cellBackground`. */
  expandRowBackground?: string;
  /** Hover state for expand rows. Default `level2`. */
  expandRowHoverBackground?: string;
};

export const DEFAULT_DELTA_APPEARANCE: Required<DeltaAppearance> = {
  cellBackground: "var(--card)",
  gridBackground: "var(--border)",
  expandRowBackground: "var(--card)",
  expandRowHoverBackground: "var(--accent)",
};

/** Report widget grids paint the card surface (see ``ReportsViewCard``). */
const REPORT_CARD_SURFACE = "var(--report-card-surface, var(--accent))";

export const WIDGET_GRID_APPEARANCE: GridAppearance = {
  rowBackground: REPORT_CARD_SURFACE,
  rowHoverBackground: "var(--report-card-surface-hover, var(--secondary))",
  headerBackground: REPORT_CARD_SURFACE,
  cellBackground: REPORT_CARD_SURFACE,
};

export type ResolvedGridAppearance = {
  rowBackground: string;
  rowHoverBackground: string;
  headerBackground: string;
  cellBackground: string;
};

export function resolveGridAppearance(
  layoutColors: ReturnType<typeof useLayoutColors>,
  appearance?: GridAppearance,
): ResolvedGridAppearance {
  const rowBackground =
    appearance?.rowBackground ??
    appearance?.cellBackground ??
    layoutColors.tableRow;
  return {
    rowBackground,
    rowHoverBackground:
      appearance?.rowHoverBackground ?? layoutColors.tableRowHover,
    headerBackground: appearance?.headerBackground ?? layoutColors.tableHeader,
    cellBackground: appearance?.cellBackground ?? rowBackground,
  };
}

export function useResolvedGridAppearance(
  appearance?: GridAppearance,
): ResolvedGridAppearance {
  const layoutColors = useLayoutColors();
  return useMemo(
    () => resolveGridAppearance(layoutColors, appearance),
    [
      layoutColors,
      appearance?.rowBackground,
      appearance?.rowHoverBackground,
      appearance?.headerBackground,
      appearance?.cellBackground,
    ],
  );
}

/** A grid row container's hover rule: its cells take ``--grid-row-hover-bg`` (``gridRowContainerStyle``). */
export const GRID_ROW_CONTAINER_CLASS =
  "[&:hover_[data-grid-cell]]:bg-(--grid-row-hover-bg)";

/** The colours a grid row container's cells read: at rest and under the pointer. */
export function gridRowContainerStyle(
  colors: ResolvedGridAppearance,
): CSSProperties {
  return {
    "--grid-row-bg": colors.rowBackground,
    "--grid-row-hover-bg": colors.rowHoverBackground,
  } as CSSProperties;
}

type GridCellBorderEdgesOptions = {
  color: string;
  showRightEdge?: boolean;
  showBottomEdge?: boolean;
  isOuterRight?: boolean;
  isOuterBottom?: boolean;
  hideOuterEdges?: boolean;
};

/** Grid cell borders: interior edges use real borders; outer edges use outside box-shadow. */
export function getGridCellBorderEdges({
  color,
  showRightEdge = true,
  showBottomEdge = true,
  isOuterRight = false,
  isOuterBottom = false,
  hideOuterEdges = false,
}: GridCellBorderEdgesOptions): Pick<
  CSSProperties,
  "borderRight" | "borderBottom" | "boxShadow"
> {
  const shadows: string[] = [];
  const result: Pick<
    CSSProperties,
    "borderRight" | "borderBottom" | "boxShadow"
  > = {};

  if (showRightEdge) {
    if (isOuterRight) {
      if (!hideOuterEdges) {
        shadows.push(`1px 0 0 0 ${color}`);
      }
    } else {
      result.borderRight = `1px solid ${color}`;
    }
  }

  if (showBottomEdge) {
    if (isOuterBottom) {
      if (!hideOuterEdges) {
        shadows.push(`0 1px 0 0 ${color}`);
      }
    } else {
      result.borderBottom = `1px solid ${color}`;
    }
  }

  if (shadows.length > 0) {
    result.boxShadow = shadows.join(", ");
  }

  return result;
}
